import { useEffect, useState, useRef } from "react";
import { useReactMediaRecorder } from "react-media-recorder";
import { useCaseContext } from "../../context/CaseContext";
import devLogger from "../../utils/devLogger";
import {
  FaMicrophoneLines,
  FaPlay,
  FaPause,
  FaStop,
  FaTrash,
  FaTrashCan,
} from "react-icons/fa6";
import unionSvg from "../../images/union-svg.svg";
import soundwaves from "../../images/sound-waveform.svg";

const RecordView = () => {
  const caseContext = useCaseContext();

  // Track if we're using an existing recording from context
  const [existingRecordingUrl, setExistingRecordingUrl] = useState<
    string | null
  >(null);
  const [displayStatus, setDisplayStatus] = useState<
    "idle" | "recording" | "stopped"
  >("idle");

  const { startRecording, stopRecording, status, clearBlobUrl, mediaBlobUrl } =
    useReactMediaRecorder({ audio: true });

  const [timer, setTimer] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Flag to control recording prevention during cleanup
  const isCleaningUp = useRef(false);

  // Check for existing recording in context when component mounts
  useEffect(() => {
    const existingRecording =
      caseContext.incidentDescription.incidentVoiceNarration;

    if (existingRecording) {
      if (Array.isArray(existingRecording) && existingRecording.length > 0) {
        // If it's an array of Files
        const file = existingRecording[0];
        if (file instanceof File || file instanceof Blob) {
          const url = URL.createObjectURL(file);
          setExistingRecordingUrl(url);
          setDisplayStatus("stopped");
        }
      } else if (existingRecording instanceof Blob) {
        // If it's a single Blob (backwards compatibility)
        const url = URL.createObjectURL(existingRecording);
        setExistingRecordingUrl(url);
        setDisplayStatus("stopped");
      }
    }

    // Create audio element once on mount
    audioRef.current = new Audio();
    audioRef.current.onended = () => setIsPlaying(false);

    // Cleanup function to revoke URLs when component unmounts
    return () => {
      if (existingRecordingUrl) {
        URL.revokeObjectURL(existingRecordingUrl);
      }

      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }

      // Stop recording if it's in progress
      if (status === "recording") {
        stopRecording();
      }
    };
    // eslint-disable-next-line
  }, []);

  // Store recording in context when a new recording is available
  useEffect(() => {
    if (mediaBlobUrl && status === "stopped" && !isCleaningUp.current) {
      saveBlobToContext(mediaBlobUrl);
      setDisplayStatus("stopped");
    }
    // eslint-disable-next-line
  }, [mediaBlobUrl, status]);

  // Update display status based on recorder status
  useEffect(() => {
    // Don't update if we're in cleanup mode
    if (isCleaningUp.current) return;

    if (status === "recording") {
      setDisplayStatus("recording");
    }
  }, [status]);

  // Helper function to save blob to context
  const saveBlobToContext = async (url: string) => {
    try {
      // Create a local File object from the blob that doesn't depend on the URL
      const audioFile = await fetch(url)
        .then((response) => response.blob())
        .then(
          (blob) =>
            new File([blob], "voice-recording.webm", {
              type: blob.type || "audio/webm",
            })
        );

      // Update the incidentVoiceNarration in the context as an array
      caseContext.updateCaseData("incidentDescription", {
        ...caseContext.incidentDescription,
        incidentVoiceNarration: [audioFile],
      });
    } catch (error) {
      devLogger.error("Error saving recording:", error);
    }
  };

  // Update timer when recording starts
  useEffect(() => {
    let interval: NodeJS.Timer | number | undefined = undefined;

    if (displayStatus === "recording") {
      interval = setInterval(() => {
        setTimer((prev) => prev + 1);
      }, 1000);
    } else if (displayStatus === "idle" || displayStatus === "stopped") {
      clearInterval(interval);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [displayStatus]);

  // Format time (MM:SS)
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  // Handle play and pause
  const handlePlayPause = () => {
    const urlToPlay = existingRecordingUrl || mediaBlobUrl;
    if (!urlToPlay || !audioRef.current) return;

    if (isPlaying) {
      // If currently playing, pause it
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      // If currently paused, play it
      // Make sure the audio element has the right source
      if (audioRef.current.src !== urlToPlay) {
        audioRef.current.src = urlToPlay;
      }

      // Use promise to handle play correctly
      const playPromise = audioRef.current.play();

      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setIsPlaying(true);
          })
          .catch((err) => {
            devLogger.error("Error playing audio:", err);
            setIsPlaying(false);
          });
      }
    }
  };

  // Clear recording both from local state and context
  const handleClearRecording = () => {
    // Set the cleanup flag to prevent race conditions
    isCleaningUp.current = true;

    // Stop playback if it's playing
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
    }

    setIsPlaying(false);
    setTimer(0);

    // Clear the blob URL
    clearBlobUrl();

    // Revoke existing URL if there is one
    if (existingRecordingUrl) {
      URL.revokeObjectURL(existingRecordingUrl);
      setExistingRecordingUrl(null);
    }

    // Reset the audio in the context
    caseContext.updateCaseData("incidentDescription", {
      ...caseContext.incidentDescription,
      incidentVoiceNarration: null,
    });

    // Update UI state
    setDisplayStatus("idle");

    // Reset cleanup flag with a slight delay to ensure all state updates have processed
    setTimeout(() => {
      isCleaningUp.current = false;
    }, 300);
  };

  return (
    <div className="w-full">
      {displayStatus === "idle" && (
        <div className="w-full flex flex-col">
          <div className="border border-gray-3 rounded-[40px] py-[6px] pl-[19px] pr-[7px] h-[52px] flex items-center justify-between">
            <p className="font-medium text-sm leading-4 text-gray-3">
              Leave your voice message up to 3 minutes
            </p>
            <button
              onClick={() => {
                if (!isCleaningUp.current) {
                  setTimer(0);
                  startRecording();
                }
              }}
              className="bg-danger rounded-[27px] p-2"
              // disabled={isCleaningUp.current}
            >
              <FaMicrophoneLines color="white" />
            </button>
          </div>
        </div>
      )}

      {displayStatus === "recording" && (
        <div className="w-full flex flex-col">
          <div className="border border-gray-3 rounded-[40px] py-[6px] pl-[19px] pr-[7px] h-[52px] flex items-center justify-between gap-[42px]">
            <div className="flex items-center gap-2.5">
              <span className="bg-[#E14942] h-[13px] w-[13px] min-w-[13px] rounded-full" />
              <p className="font-normal text-2xl leading-4 text-black">
                {formatTime(timer)}
              </p>
              <div className="flex-grow overflow-x-hidden">
                <img src={soundwaves} alt="Soundwaves" />
              </div>
            </div>

            <div className="flex relative min-w-[147.32px]">
              <img src={unionSvg} alt="Union" className="w-[147.32px]" />
              <div className="absolute top-[2.92px] left-[2.92px] border-[1.46px] border-black w-[91.89px] h-[35.01px] rounded-[17.5px] bg-[#ECECEC] flex justify-center items-center">
                <button
                  onClick={handleClearRecording}
                  className="flex justify-center items-center bg-danger/70 rounded-[27px] p-2 h-[23px] w-[23px]"
                >
                  <FaTrash color="white" className="h-[14px]" />
                </button>
              </div>

              <button
                onClick={stopRecording}
                className="flex justify-center items-center bg-[#ECECEC] border-[0.73px] border-[#0000000A] rounded-[27px] h-[35.01px] w-[35.01px] absolute top-[2.92px] right-[3.92px]"
              >
                <FaStop color="#131313" className="h-[14px]" />
              </button>
            </div>
          </div>
        </div>
      )}

      {displayStatus === "stopped" && (
        <div className="w-full flex flex-col">
          <div className="border border-gray-3 rounded-[40px] py-[6px] pl-[19px] pr-[7px] h-[52px] flex items-center justify-between gap-[15px]">
            <div className="flex items-center gap-0.5">
              <button
                onClick={handlePlayPause}
                className="flex justify-center items-center rounded-[27px] h-[24px] w-[24px]"
              >
                {isPlaying ? (
                  <FaPause className="h-[14px]" />
                ) : (
                  <FaPlay className="h-[14px]" />
                )}
              </button>

              <div className="flex-grow overflow-x-hidden">
                <img src={soundwaves} alt="Soundwaves" />
              </div>
            </div>

            <div className="flex min-w-[52.1px]">
              <button
                onClick={handleClearRecording}
                className="flex justify-center items-center bg-danger border-[1.68px] border-[#000000CC] rounded-[20.17px] h-[40.34px] w-[52.1px]"
              >
                <FaTrashCan color="#D9D9D9" className="h-[19px]" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecordView;
